/**
 * Calculate if an activity is full
 * @param {Object} activity - The activity object
 * @returns {boolean} - True if the activity is full
 */
export const isActivityFull = (activity) => {
  return activity.currentParticipants >= activity.maxParticipants;
};

/**
 * Calculate available spots for an activity
 * @param {Object} activity - The activity object
 * @returns {number} - Number of available spots
 */
export const getAvailableSpots = (activity) => {
  return activity.maxParticipants - activity.currentParticipants;
};

/**
 * Calculate available spots for modification
 * @param {Object} activity - The activity object
 * @param {number} currentUserParticipants - Current number of participants for the user
 * @returns {number} - Number of available spots including user's current spots
 */
export const getAvailableSpotsForModification = (activity, currentUserParticipants) => {
  // When modifying, we need to consider that the user's current spots will be freed up
  const spotsWithoutUser = activity.currentParticipants - currentUserParticipants;
  return activity.maxParticipants - spotsWithoutUser;
};

/**
 * Get the spots label for display
 * @param {Object} activity - The activity object
 * @param {Function} t - Translation function
 * @returns {string} - Label to display
 */
export const getSpotsLabel = (activity, t) => {
  if (isActivityFull(activity)) return t('full');
  if (activity.category === 'Shiatsu' || activity.category === 'Chi Nei Tsang' || activity.category === 'Soin') {
    return t('available');
  }
  return t('spotsLeft', { count: getAvailableSpots(activity) });
};

/**
 * Calculate maximum allowed participants for modification
 * @param {Object} activity - The activity object
 * @param {number} currentParticipants - Current number of participants for the user
 * @returns {number} - Maximum allowed participants
 */
export const getMaxAllowedParticipants = (activity, currentParticipants) => {
  if (activity.category === 'Shiatsu' || activity.category === 'Chi Nei Tsang' || activity.category === 'Soin') {
    return 1;
  }
  // The maximum should be the minimum between:
  // 1. The global max of 4 participants
  // 2. The available spots + current user's spots (to allow them to keep their current spots)
  return Math.min(4, getAvailableSpots(activity) + currentParticipants);
};

/**
 * Get participants display text
 * @param {Object} activity - The activity object
 * @param {Function} t - Translation function
 * @returns {string} - Text to display
 */
export const getParticipantsText = (activity, t) => {
  if (activity.category === 'Shiatsu' || activity.category === 'Chi Nei Tsang' || activity.category === 'Soin') {
    return t('available');
  }
  return t('participantsCount', {
    current: activity.currentParticipants,
    max: activity.maxParticipants
  });
}; 